import React, { useEffect, useRef } from "react";
import { VisuallyHidden } from "@chakra-ui/react";
import { animate, useMotionValue } from "framer-motion";
import { ease, useReducedMotion } from "../../theme/foundations/motion";

export interface CountUpProps {
  /** Target number (strings are coerced; NaN → 0). */
  value: number | string;
  /** Formatter, e.g. formatAmount / formatQuantity. Output at rest is exactly `format(Number(value))`. */
  format?: (n: number) => string;
  /** Seconds (default 0.9). */
  duration?: number;
  className?: string;
}

const defaultFormat = (n: number) => String(Math.round(n));

/**
 * Counting numeral (framer-motion 6 `useMotionValue` + `animate`; the text is written straight to the DOM node,
 * so React does not re-render per frame). Under prefers-reduced-motion it renders the final value immediately.
 * @example <Text textStyle="display.lg"><CountUp value={overdue} format={formatAmount} /></Text>
 */
export const CountUp: React.FC<CountUpProps> = ({ value, format, duration = 0.9, className }) => {
  const reduce = useReducedMotion();
  const target = Number(value) || 0;
  const fmt = format ?? defaultFormat;
  const formatRef = useRef(fmt);
  formatRef.current = fmt;

  const spanRef = useRef<HTMLSpanElement>(null);
  const mv = useMotionValue(reduce ? target : 0);

  useEffect(() => {
    const write = (v: number) => {
      if (spanRef.current) spanRef.current.textContent = formatRef.current(v);
    };
    const unsubscribe = mv.onChange(write);
    if (reduce) {
      mv.set(target);
      write(target);
      return unsubscribe;
    }
    const controls = animate(mv, target, { duration, ease: ease.standard });
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [target, duration, reduce, mv]);

  // Server/first paint: show the formatted current value (0 before the animation starts, or the target under reduced motion).
  // The animating span is hidden from AT; the settled value is exposed once, so intermediate counts are never read.
  return (
    <>
      <span ref={spanRef} className={className} aria-hidden>
        {fmt(mv.get())}
      </span>
      <VisuallyHidden>{fmt(target)}</VisuallyHidden>
    </>
  );
};
